import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import axios from 'axios';

const BillingDashboard = () => {
  const navigate = useNavigate();
  const [overview, setOverview] = useState([]);
  const [currentTenantUsage, setCurrentTenantUsage] = useState(null);
  const [usageHistory, setUsageHistory] = useState([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState(null);
  const [selectedTenant, setSelectedTenant] = useState('default');

  const backendUrl = process.env.REACT_APP_BACKEND_URL;
  const token = localStorage.getItem('token');
  const user = JSON.parse(localStorage.getItem('user') || '{}');
  const isSuperAdmin = user.role === 'super_admin';

  useEffect(() => {
    if (!token) {
      alert('Please login first');
      navigate('/login');
      return;
    }
    fetchBillingData();
  }, [token, navigate]);

  const fetchBillingData = async (tenantIdOverride = null) => {
    try {
      setLoading(true);
      setError(null);

      if (isSuperAdmin) {
        // Fetch billing overview for all tenants
        const overviewResponse = await axios.get(`${backendUrl}/api/admin/billing/overview`, {
          headers: { 
            'Authorization': `Bearer ${token}`,
            'Content-Type': 'application/json'
          }
        });
        setOverview(overviewResponse.data);
      }

      // Fetch current tenant usage - use override if provided
      const tenantId = tenantIdOverride || selectedTenant || user.tenant_id || 'default';
      const usageResponse = await axios.get(`${backendUrl}/api/admin/tenants/${tenantId}/usage`, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      setCurrentTenantUsage(usageResponse.data);

      // Fetch usage history
      const historyResponse = await axios.get(`${backendUrl}/api/admin/tenants/${tenantId}/usage/history?months=6`, {
        headers: { 
          'Authorization': `Bearer ${token}`,
          'Content-Type': 'application/json'
        }
      });
      setUsageHistory(historyResponse.data);

    } catch (err) {
      console.error('Error fetching billing data:', err);
      const errorMsg = err.response?.data?.detail || err.message || 'Failed to fetch billing data';
      setError(errorMsg);
      if (err.response?.status === 403) {
        alert('Access denied.');
        navigate('/dashboard');
      }
    } finally {
      setLoading(false);
    }
  };

  const formatNumber = (num) => {
    return num?.toLocaleString() || '0';
  };

  const calculateCost = (tokens) => {
    // Example: $0.000002 per token
    return (tokens * 0.000002).toFixed(4);
  };

  if (error && !currentTenantUsage) {
    return (
      <div className="min-h-screen bg-gray-100 p-6">
        <div className="max-w-7xl mx-auto">
          <div className="bg-red-50 border border-red-200 rounded-lg p-6">
            <h2 className="text-red-800 font-semibold mb-2">Error</h2>
            <p className="text-red-600">{error}</p>
            <button
              onClick={() => navigate('/admin')}
              className="mt-4 px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
            >
              Back to Admin
            </button>
          </div>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gray-100 p-6">
      <div className="max-w-7xl mx-auto">
        <div className="flex justify-between items-center mb-8">
          <h1 className="text-3xl font-bold text-gray-900">Billing & Usage</h1>
          <div className="flex gap-3">
            {isSuperAdmin && (
              <>
                <button
                  onClick={() => navigate('/admin/module-wallets')}
                  className="px-4 py-2 bg-gradient-to-r from-blue-600 to-purple-600 text-white rounded hover:from-blue-700 hover:to-purple-700 flex items-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M3 10h18M7 15h1m4 0h1m-7 4h12a3 3 0 003-3V8a3 3 0 00-3-3H6a3 3 0 00-3 3v8a3 3 0 003 3z" />
                  </svg>
                  Module Wallets
                </button>
                <button
                  onClick={() => navigate('/admin/module-pricing')}
                  className="px-4 py-2 bg-gradient-to-r from-green-600 to-blue-600 text-white rounded hover:from-green-700 hover:to-blue-700 flex items-center gap-2"
                >
                  <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M10.325 4.317c.426-1.756 2.924-1.756 3.35 0a1.724 1.724 0 002.573 1.066c1.543-.94 3.31.826 2.37 2.37a1.724 1.724 0 001.065 2.572c1.756.426 1.756 2.924 0 3.35a1.724 1.724 0 00-1.066 2.573c.94 1.543-.826 3.31-2.37 2.37a1.724 1.724 0 00-2.572 1.065c-.426 1.756-2.924 1.756-3.35 0a1.724 1.724 0 00-2.573-1.066c-1.543.94-3.31-.826-2.37-2.37a1.724 1.724 0 00-1.065-2.572c-1.756-.426-1.756-2.924 0-3.35a1.724 1.724 0 001.066-2.573c-.94-1.543.826-3.31 2.37-2.37.996.608 2.296.07 2.572-1.065z" />
                    <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M15 12a3 3 0 11-6 0 3 3 0 016 0z" />
                  </svg>
                  Module Pricing & Discounts
                </button>
              </>
            )}
            <button
              onClick={() => navigate('/admin')}
              className="px-4 py-2 bg-gray-600 text-white rounded hover:bg-gray-700"
            >
              Back to Admin
            </button>
          </div>
        </div>

        {/* Tenant Selector for Super Admin */}
        {isSuperAdmin && overview.length > 0 && (
          <div className="bg-white rounded-lg shadow p-4 mb-6">
            <label className="block text-sm font-medium text-gray-700 mb-2">
              Select Tenant to View Usage:
            </label>
            <select
              value={selectedTenant}
              onChange={(e) => {
                setSelectedTenant(e.target.value);
                fetchBillingData(e.target.value);
              }}
              className="w-full md:w-1/2 px-4 py-2 border border-gray-300 rounded-lg focus:ring-2 focus:ring-blue-500"
            >
              <option value="default">Default Tenant</option>
              {overview.map((tenant) => (
                <option key={tenant.tenant_id} value={tenant.tenant_id}>
                  {tenant.tenant_name} ({tenant.plan})
                </option>
              ))}
            </select>
          </div>
        )}

        {loading ? (
          <div className="text-center py-12">Loading...</div>
        ) : (
          <>
            {/* Current Month Usage */}
            {currentTenantUsage && (
              <div className="bg-white rounded-lg shadow p-6 mb-6">
                <h2 className="text-xl font-semibold mb-4">Current Month Usage</h2>
                <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
                  <div className="border rounded-lg p-4">
                    <p className="text-sm text-gray-500">AI Tokens Used</p>
                    <p className="text-2xl font-bold text-blue-600 mt-1">
                      {formatNumber(currentTenantUsage.ai_tokens_used)}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      Quota: {formatNumber(currentTenantUsage.quota?.ai_tokens_monthly || 0)}
                    </p>
                  </div>
                  <div className="border rounded-lg p-4">
                    <p className="text-sm text-gray-500">Opinions Generated</p>
                    <p className="text-2xl font-bold text-green-600 mt-1">
                      {currentTenantUsage.opinions_generated || 0}
                    </p>
                  </div>
                  <div className="border rounded-lg p-4">
                    <p className="text-sm text-gray-500">Storage Used</p>
                    <p className="text-2xl font-bold text-purple-600 mt-1">
                      {(currentTenantUsage.storage_used_mb || 0).toFixed(2)} MB
                    </p>
                    <p className="text-xs text-gray-500 mt-1">
                      Limit: {formatNumber(currentTenantUsage.quota?.storage_limit_mb || 0)} MB
                    </p>
                  </div>
                  <div className="border rounded-lg p-4">
                    <p className="text-sm text-gray-500">Estimated Cost</p>
                    <p className="text-2xl font-bold text-orange-600 mt-1">
                      ${calculateCost(currentTenantUsage.ai_tokens_used || 0)}
                    </p>
                    <p className="text-xs text-gray-500 mt-1">This month</p>
                  </div>
                </div>
              </div>
            )}

            {/* Usage History */}
            {usageHistory.length > 0 && (
              <div className="bg-white rounded-lg shadow p-6 mb-6">
                <h2 className="text-xl font-semibold mb-4">Usage History (Last 6 Months)</h2>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Month</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">AI Tokens</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Opinions</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Storage (MB)</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Est. Cost</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {usageHistory.map((record, idx) => (
                        <tr key={idx}>
                          <td className="px-4 py-3 whitespace-nowrap">{record.month}</td>
                          <td className="px-4 py-3 whitespace-nowrap">{formatNumber(record.ai_tokens_used)}</td>
                          <td className="px-4 py-3 whitespace-nowrap">{record.opinions_generated || 0}</td>
                          <td className="px-4 py-3 whitespace-nowrap">{(record.storage_used_mb || 0).toFixed(2)}</td>
                          <td className="px-4 py-3 whitespace-nowrap">${calculateCost(record.ai_tokens_used || 0)}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}

            {/* All Tenants Overview (Super Admin Only) */}
            {isSuperAdmin && overview.length > 0 && (
              <div className="bg-white rounded-lg shadow p-6">
                <h2 className="text-xl font-semibold mb-4">All Tenants Overview</h2>
                <div className="overflow-x-auto">
                  <table className="w-full">
                    <thead className="bg-gray-50">
                      <tr>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Tenant</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Plan</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Status</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">AI Tokens</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Opinions</th>
                        <th className="px-4 py-2 text-left text-xs font-medium text-gray-500 uppercase">Est. Cost</th>
                      </tr>
                    </thead>
                    <tbody className="divide-y divide-gray-200">
                      {overview.map((tenant, idx) => (
                        <tr key={idx}>
                          <td className="px-4 py-3 whitespace-nowrap font-medium">{tenant.tenant_name}</td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className="px-2 py-1 text-xs rounded bg-blue-100 text-blue-800">
                              {tenant.plan}
                            </span>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">
                            <span className={`px-2 py-1 text-xs rounded ${
                              tenant.status === 'active' ? 'bg-green-100 text-green-800' : 'bg-red-100 text-red-800'
                            }`}>
                              {tenant.status}
                            </span>
                          </td>
                          <td className="px-4 py-3 whitespace-nowrap">{formatNumber(tenant.ai_tokens_used)}</td>
                          <td className="px-4 py-3 whitespace-nowrap">{tenant.opinions_generated || 0}</td>
                          <td className="px-4 py-3 whitespace-nowrap">${tenant.estimated_cost || '0.00'}</td>
                        </tr>
                      ))}
                    </tbody>
                  </table>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
};

export default BillingDashboard;
